import { Observable } from 'rxjs';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';

import { MenuItemsGuichet } from '../menu-items/menu-item-guichet';
import { MenuItemsEnvoi } from '../menu-items/menu-item-envoi';
import { MenuItemsChef } from '../menu-items/menu-item-chef';
import { MenuItemsRetrait } from '../menu-items/menu-item-retrait';
import { MenuItemsAccounting } from '../menu-items/menu-item-accounting';
import { MenuItemsChefCourrier } from '../menu-items/menu-item-chefcourrier';
import { MenuItemsFiscalite } from '../menu-items/menu-item-fiscalite';
import { MenuItemsDispatching } from '../menu-items/menu-item-dispatching';

@Injectable({
  providedIn: 'root'
})
export class RoleGuard implements CanActivate {

  private userprivileges: any;
  constructor(
    private router: Router,
    private toastService: ToastrService,
    private guichetMenu: MenuItemsGuichet,
    private envoiMenu: MenuItemsEnvoi,
    private chefMenu: MenuItemsChef,
    private retraitMenu: MenuItemsRetrait,
    private accountingMenu: MenuItemsAccounting,
    private chefCourrierMenu: MenuItemsChefCourrier,
    private fiscaliteMenu: MenuItemsFiscalite,
    private dispatchingMenu: MenuItemsDispatching
  ) { }

  canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean {
    this.userprivileges = localStorage.getItem('authority');
    this.userprivileges = JSON.parse(this.userprivileges);
    const section = localStorage.getItem("section");
    const userRole = localStorage.getItem("userRole");
    const url = state.url;

    const isAdmin = (section === 'admin') || (userRole && userRole.toLowerCase().includes('admin'));
    if (isAdmin) {
      return true;
    }

    if (section && section !== 'admin') {
      // Build map of section to its menu items
      const sectionMenus: { [key: string]: any[] } = {
        envoi: this.envoiMenu.getMenuitem(),
        retrait: this.retraitMenu.getMenuitem(),
        guichet: this.guichetMenu.getMenuitem(),
        chef: this.chefMenu.getMenuitem(),
        accounting: this.accountingMenu.getMenuitem(),
        chefcourrier: this.chefCourrierMenu.getMenuitem(),
        fiscalite: this.fiscaliteMenu.getMenuitem(),
        dispatching: this.dispatchingMenu.getMenuitem()
      };

      let longestMatchLength = -1;
      let matchedSection: string | null = null;
      let matchesCurrentSection = false;

      for (const sec of Object.keys(sectionMenus)) {
        const items = sectionMenus[sec];
        if (!items) continue;
        for (const item of items) {
          if (item.state) {
            const routePath = '/' + item.state.replace(/\\/g, '');
            if (url === routePath || url.startsWith(routePath + '/') || url.startsWith(routePath + '?')) {
              if (sec === section) {
                matchesCurrentSection = true;
              }
              if (routePath.length > longestMatchLength) {
                longestMatchLength = routePath.length;
                matchedSection = sec;
              }
            }
          }
          if (item.children) {
            for (const child of item.children) {
              if (child.state) {
                const routePath = '/' + child.state.replace(/\\/g, '');
                if (url === routePath || url.startsWith(routePath + '/') || url.startsWith(routePath + '?')) {
                  if (sec === section) {
                    matchesCurrentSection = true;
                  }
                  if (routePath.length > longestMatchLength) {
                    longestMatchLength = routePath.length;
                    matchedSection = sec;
                  }
                }
              }
            }
          }
        }
      }

      // If the URL matches a menu item of another section but NOT the current section, restrict it
      if (!matchesCurrentSection && matchedSection && matchedSection !== section) {
        this.showSectionBlockedError();
        return false;
      }
    }

    if (this.userprivileges) {
      for (const r of this.userprivileges) {
        if (r.privileges && r.state) {
          // If the route's required privilege matches the user's granted privilege
          if (r.privileges.codepriv == next.data.role || (section === 'chefcourrier' && r.privileges.codepriv == 'up_colis')) {
            // Remove hardcoded section blocking for cr_colis and cr_passager
            // since we now rely entirely on dynamic menus and matching logic.
            return true;
          }
        }
      }
      
      // If the route doesn't strictly require a specific next.data.role, or if we want dynamic fallback
      // check if the user holds ANY privilege that maps to this URL in their current section
      if (section && section !== 'admin') {
          const sectionMenus: { [key: string]: any[] } = {
            envoi: this.envoiMenu.getMenuitem(),
            retrait: this.retraitMenu.getMenuitem(),
            guichet: this.guichetMenu.getMenuitem(),
            chef: this.chefMenu.getMenuitem(),
            accounting: this.accountingMenu.getMenuitem(),
            chefcourrier: this.chefCourrierMenu.getMenuitem(),
            fiscalite: this.fiscaliteMenu.getMenuitem(),
            dispatching: this.dispatchingMenu.getMenuitem()
          };
         const items = sectionMenus[section];
         if (items) {
           for (const item of items) {
             const routePath = '/' + item.state.replace(/\\/g, '');
             if (url === routePath || url.startsWith(routePath + '/') || url.startsWith(routePath + '?')) {
                // Check if user has item.priv
                const hasPriv = this.userprivileges.some((p: any) => p.state && p.privileges?.codepriv === item.priv);
                if (hasPriv) return true;
             }
             if (item.children) {
               for (const child of item.children) {
                 const childRoutePath = '/' + child.state.replace(/\\/g, '');
                 if (url === childRoutePath || url.startsWith(childRoutePath + '/') || url.startsWith(childRoutePath + '?')) {
                    const hasPriv = this.userprivileges.some((p: any) => p.state && p.privileges?.codepriv === child.priv);
                    if (hasPriv) return true;
                 }
               }
             }
           }
         }
      }
    }

    // navigate to not found page
    this.toastService.error(
      'SVP veillez contacter l\'administrateur pour plus d\'informations',
      'POSTE NON AUTORISE',
      {
        timeOut: 5000,
        progressBar: true,
        positionClass: "toast-bottom-right",
        enableHtml: true,
      }
    );
    return false;
  }

  private showSectionBlockedError(): void {
    this.toastService.error(
      'Vous ne pouvez pas accéder à cette page depuis votre section active.',
      'ACCÈS INTERDIT',
      {
        timeOut: 5000,
        progressBar: true,
        positionClass: "toast-bottom-right",
        enableHtml: true,
      }
    );
  }
}
